import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import formatTimestamp from '../../../i18n/formatTimestamp';

import {
  DataTable,
  DataTableCell,
  DataTableColumn,
  Tooltip
} from '@salesforce/design-system-react';

import './SelectionTable.scss';

const CustomDataTableCell = ({ children, property, ...props }) => {
  const onClick = () => {
    if (props.onClick) {
      props.onClick(props.item.name);
    }
  };

  const selectedClass = props.item.selected ? 'selected-cell' : '';
  switch (property) {
    case 'name':
      return (
        <td className={`truncate name-cell ${selectedClass}`} onClick={onClick}>
          {children}
        </td>
      );
    case 'highlight':
      if (props.item.selected) {
        return <td className="highlight-cell" onClick={onClick} />;
      } else {
        return <td onClick={onClick} />;
      }
    case 'state':
      let stateClass = '';
      if (props.item.state === 'selected') {
        stateClass = 'selected-text';
      } else if (props.item.state === 'notEligible') {
        stateClass = 'ineligible-text';
      }
      return (
        <td className={`${stateClass} ${selectedClass}`} onClick={onClick}>
          {props.item.localizedState}
        </td>
      );
    case 'thumbnail':
      return (
        <td className={`thumbnail-cell ${selectedClass}`} onClick={onClick}>
          <div
            className="thumbnail-container"
            style={{
              backgroundImage: `url(${props.item.asset.image_link})`
            }}
          />
        </td>
      );
    case 'explanation':
      return <td className="wrap-text">{children}</td>;
    default: {
      return (
        <td className={`${selectedClass}`} onClick={onClick}>
          {children}
        </td>
      );
    }
  }
};

CustomDataTableCell.displayName = DataTableCell.displayName;
CustomDataTableCell.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
    PropTypes.bool
  ]),
  property: PropTypes.string.isRequired,
  formatMessage: PropTypes.func,
  item: PropTypes.object
};

class SelectionTable extends Component {
  headerCell(property, formatMessage, tooltip) {
    const text = formatMessage({
      id: `components.ecs.selections.columns.${property}`
    });
    if (tooltip) {
      const tooltipText = formatMessage({ id: tooltip });
      return (
        <span>
          {text}
          <Tooltip
            align="right"
            content={tooltipText}
            position="overflowBoundaryElement"
          />
        </span>
      );
    }

    return text;
  }

  columns() {
    return {
      highlight: { sortable: false, width: '5px' },
      thumbnail: { sortable: false, width: '42px' },
      name: { sortable: false, width: '175px' },
      state: { sortable: false, width: '115px' },
      rules: {
        sortable: false,
        width: '280px',
        tooltip: 'components.ecs.selectionBrowser.details.rules.tooltip'
      },
      explanation: { sortable: false }
    };
  }

  parseRule(explanation) {
    const { formatMessage } = this.props.intl;
    if (explanation) {
      let rule;
      if (explanation.startsWith('Not a child of')) {
        rule = ['parentchild', explanation];
      } else if (explanation.startsWith('Asset start date is after')) {
        const selectedAt = formatTimestamp(this.props.intl)(
          this.props.block.selection.timestamp
        );
        const explainMessage = formatMessage(
          {
            id:
              'components.ecs.selectionBrowser.details.rules.startdateafter.rule'
          },
          { datetime: selectedAt }
        );
        rule = ['', explainMessage];
      } else if (explanation.startsWith('Asset end date is before')) {
        const selectedAt = formatTimestamp(this.props.intl)(
          this.props.block.selection.timestamp
        );
        const explainMessage = formatMessage(
          {
            id:
              'components.ecs.selectionBrowser.details.rules.enddatebefore.rule'
          },
          { datetime: selectedAt }
        );
        rule = ['', explainMessage];
      } else if (
        explanation.startsWith(
          'Excluded because of WeatherRule:' //'Forecasted Temperature was'
        )
      ) {
        if (explanation.includes('Temperature')) {
          const temps = explanation.match(/-?\d{1,3}\./g);
          const explainMessage = formatMessage(
            {
              id:
                'components.ecs.selectionBrowser.details.rules.weather.temperature'
            },
            {
              forecast: temps[0].replace(/\./, '°'),
              low: temps[1].replace(/\./, '°'),
              high: temps[2].replace(/\./, '°')
            }
          );
          rule = ['', explainMessage];
        } else if (explanation.includes('Precipitation')) {
          const percent = explanation.match(/\d{1,3}\./g);
          const greaterOrLesser = explanation.match(/(Greater)|(Less)/)[0];
          const precipThan = formatMessage({
            id: `components.ecs.selectionBrowser.details.rules.weather.${greaterOrLesser}`
          });
          const explainMessage = formatMessage(
            {
              id:
                'components.ecs.selectionBrowser.details.rules.weather.precipitation'
            },
            {
              forecast: percent[0].replace(/\./, '%'),
              precipThan: precipThan,
              value: percent[1].replace(/\./, '%')
            }
          );
          rule = ['', explainMessage];
        } else if (explanation.includes('Forecast was')) {
          const forecastTypes = explanation
            .match(/highTemp:|precipitation:|rain:|snow:|ice:|thunderstorm:/g)
            .filter(item => item)
            .map(string => string.slice(0, -1));
          const assetForecastTypes = explanation
            .match(
              /highTemp\s|precipitation\s|rain\s|snow\s|ice\s|thunderstorm\s/g
            )
            .filter(item => item)
            .map(string => string.slice(0, -1));
          if (
            forecastTypes[0] === 'highTemp' ||
            forecastTypes[0] === 'lowTemp'
          ) {
            const forecastTemps = explanation.match(
              /(\[highTemp:\s|lowTemp:\s)([^[\]]+)\]/
            )[0];
            var forecastTempLabels = '';
            forecastTempLabels = forecastTemps.replace(
              /highTemp:\s|lowTemp:\s/,
              ''
            );
            forecastTempLabels = forecastTempLabels.replace(/\.\d/g, '°');

            const assetTemps = explanation.match(
              /(\[highTemp\s|lowTemp\sbetween\s)([^[\]]+)\]/g
            );
            var assetTempLabels = '';
            for (let idx = 0; idx <= assetTemps.length; idx += 1) {
              const orString = formatMessage({
                id: 'components.ecs.selectionBrowser.details.rules.weather.or'
              });
              if (!assetTemps[idx]) {
                break;
              } else if (idx > 0) {
                assetTempLabels = assetTempLabels.concat(orString);
              }
              const temp = assetTemps[idx].match(/-?\d{1,3}\./g);
              const assetTempLabel = formatMessage(
                {
                  id:
                    'components.ecs.selectionBrowser.details.rules.weather.assetTemperature'
                },
                {
                  low: temp[0].replace(/\./, '°'),
                  high: temp[1].replace(/\./, '°')
                }
              );
              assetTempLabels = assetTempLabels.concat(assetTempLabel);
            }
            const explainMessage = formatMessage(
              {
                id:
                  'components.ecs.selectionBrowser.details.rules.weather.temperature'
              },
              {
                forecast: forecastTempLabels,
                assetTemperature: assetTempLabels
              }
            );
            rule = ['', explainMessage];
          } else {
            const forecastPrecip = explanation.match(
              /(rain:\s|snow:\s|ice:\s|thunderstorm:\s|precipitation:\s)([^[\]]+)\]/g
            );
            const assetPrecips = explanation.match(
              /(rain\s|snow\s|ice\s|thunderstorm\s|precipitation\s)([^[\]]+)\]/g
            );
            var forecastedTypeLabels = '';
            for (let idx = 0; idx < forecastPrecip.length; idx += 1) {
              if (!forecastPrecip[idx]) {
                break;
              } else if (idx > 0) {
                forecastedTypeLabels = forecastedTypeLabels.concat(', ');
              }
              var forecastPrecips = forecastPrecip[idx].replace(
                /(rain:\s|snow:\s|ice:\s|thunderstorm:\s|precipitation:\s)/,
                ''
              );
              forecastPrecips = forecastPrecips.replace(/(\.\d)/g, '%');
              const forecastTypeLabel = formatMessage({
                id: `components.ecs.selectionBrowser.details.rules.weather.forecastType.${forecastTypes[idx]}`
              });
              const forecastPrecipLabel = formatMessage(
                {
                  id:
                    'components.ecs.selectionBrowser.details.rules.weather.precipitationForecast'
                },
                {
                  forecastType: forecastTypeLabel,
                  forecast: forecastPrecips
                }
              );
              forecastedTypeLabels = forecastedTypeLabels.concat(
                forecastPrecipLabel
              );
            }
            var assetPrecipitationLabels = '';
            for (let idx = 0; idx < assetForecastTypes.length; idx += 1) {
              const greaterOrLesser = assetPrecips[idx].match(
                /(greater)|(less)/
              )[0];
              const percent = assetPrecips[idx].match(/\d{1,3}\./)[0];
              const orString = formatMessage({
                id: 'components.ecs.selectionBrowser.details.rules.weather.or'
              });
              if (!assetForecastTypes[idx]) {
                break;
              } else if (idx > 0) {
                assetPrecipitationLabels = assetPrecipitationLabels.concat(
                  orString
                );
              }
              const assetPrecipitationLabel = formatMessage({
                id: `components.ecs.selectionBrowser.details.rules.weather.forecastType.${assetForecastTypes[idx]}`
              });
              const precipThan = formatMessage({
                id: `components.ecs.selectionBrowser.details.rules.weather.${greaterOrLesser}`
              });
              const assetPrecipLabel = formatMessage(
                {
                  id:
                    'components.ecs.selectionBrowser.details.rules.weather.assetPrecipitation'
                },
                {
                  forecastType: assetPrecipitationLabel,
                  precipThan: precipThan,
                  value: percent.replace(/\./, '%')
                }
              );
              assetPrecipitationLabels = assetPrecipitationLabels.concat(
                assetPrecipLabel
              );
            }
            const explainMessage = formatMessage(
              {
                id:
                  'components.ecs.selectionBrowser.details.rules.weather.precipitation'
              },
              {
                forecastTypes: forecastedTypeLabels,
                assetPrecipitation: assetPrecipitationLabels
              }
            );
            rule = ['', explainMessage];
          }
        } else if (explanation.includes('Alarms')) {
          const alarm = explanation.match(/\[(.*?)\]/g);
          const noAlarm = formatMessage({
            id: 'components.ecs.selectionBrowser.details.rules.weather.none'
          });
          const alarmForcast = alarm[0] === '[]' ? noAlarm : alarm[0];
          const explainMessage = formatMessage(
            {
              id: 'components.ecs.selectionBrowser.details.rules.weather.alarms'
            },
            { forecast: alarmForcast, value: alarm[1] }
          );
          rule = ['', explainMessage];
        } else if (explanation.includes('Index')) {
          const windexValue = explanation.match(/\[(.*?)\]/g);
          const windex = explanation.match(/Index\s(.*?)\swas/)[1];
          const windexName = formatMessage({
            id: `components.ecs.attributes.modal.weather.indexTypes.${windex}`
          });
          const explainMessage = formatMessage(
            {
              id:
                'components.ecs.selectionBrowser.details.rules.weather.indices'
            },
            {
              windex: windexName,
              forecast: windexValue[0],
              value: windexValue[1]
            }
          );
          rule = ['', explainMessage];
        }
      } else {
        rule = explanation.replaceAll('Excluded because of ', '').split(':');
      }
      return {
        rule: rule[0].toLowerCase().replaceAll('rule', ''),
        explain: rule[1]
      };
    }
  }

  tableData() {
    const { formatMessage } = this.props.intl;
    const { selection } = this.props.block || {};
    const hasFallback = this.props.block?.selection?.source === 'Fallback';
    if (!selection) {
      return [];
    }

    const assets = this.props.assets || [];
    const arms = [];
    Object.keys(selection?.arms).forEach(arm => {
      let explanation = selection.arms[arm];
      let state = 'eligible';
      let sort = 1;
      let rules = '-';
      const selected = arm === this.props.selectedArm;

      const selectionAsset = selection?.asset || {};

      // Empty pixel or Contingency selected
      if (selection?.asset === null) {
        selectionAsset.key = formatMessage({
          id: 'components.ecs.selectionBrowser.assetDetails.noneSelected'
        });
        selectionAsset.uuid = '0';
      }

      if (arm === selectionAsset.key) {
        state = 'selected';
        sort = 0;
      }

      const asset = assets.find(asset => asset.key === arm);

      if (asset) {
        const fallbackAsset =
          hasFallback && asset.key === this.props.block.selection.asset.key;
        if (explanation) {
          const parsed = this.parseRule(explanation);
          explanation = parsed.explain;
          state = 'notEligible';
          sort = 2;
          if (parsed.rule) {
            rules = formatMessage({
              id: `components.ecs.selectionBrowser.details.rules.${parsed.rule}.rule`
            });
          }
        }

        if (fallbackAsset) {
          explanation = formatMessage({
            id: 'components.ecs.selectionBrowser.details.fallback'
          });
          state = 'selected';
          sort = 0;
          rules = '';
        }
        const item = {
          id: `${this.props.job.send.id}-${this.props.block.block_id}:${arm}`,
          name: arm,
          state,
          sort,
          localizedState: formatMessage({
            id: `components.ecs.selectionBrowser.details.states.${state}`
          }),
          rules,
          explanation: explanation || '-',
          asset,
          selected
        };

        arms.push(item);
      }
    });

    arms.sort((a, b) => a.sort - b.sort);

    return arms;
  }

  render() {
    const items = this.tableData();
    const { formatMessage } = this.props.intl;
    const paneState = this.props.selectedArm
      ? 'asset-pane-open'
      : 'asset-pane-closed';

    return (
      <div className={`selection-table-container ${paneState}`}>
        <DataTable
          items={items}
          className="selection-table"
          fixedLayout
          fixedHeader
          noRowHover
        >
          {Object.entries(this.columns()).map((column, index) => {
            const sortable = column[1].sortable;
            const width = column[1].width;
            const property = column[0];
            const tooltip = column[1].tooltip;
            return (
              <DataTableColumn key={index} sortable={sortable}>
                <CustomDataTableCell
                  property={property}
                  formatMessage={formatMessage}
                  label={this.headerCell(property, formatMessage, tooltip)}
                  width={width}
                  onClick={this.props.onRowSelect}
                />
              </DataTableColumn>
            );
          })}
        </DataTable>
      </div>
    );
  }
}

SelectionTable.contextTypes = {
  router: PropTypes.object
};

SelectionTable.propTypes = {
  actions: PropTypes.object,
  assets: PropTypes.array,
  job: PropTypes.object,
  block: PropTypes.object,
  onRowSelect: PropTypes.func.isRequired,
  onAction: PropTypes.func,
  selectedArm: PropTypes.string,
  location: PropTypes.object
};

export default injectIntl(SelectionTable);
